import React, { useEffect, useState } from "react";
import api from "../../api";
import { useAlert } from "react-alert";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import copy from "copy-to-clipboard";
import { AddSummary } from "./adminComp/addSummaryModal";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { ChartNoAxesCombined } from 'lucide-react';
import './TradeSummary.css'

function TradeSummary({ adminType }) {
  const alert = useAlert();
  const [signal, setSignal] = useState("");
  const [summary, setSummary] = useState("");
  const [loader, setLoader] = useState(false);
  const [copyText, setCopyText] = useState("");
  const [show, setShow] = useState(false);
  const [characterCount, setCharacterCount] = useState(0);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const getSummary = async () => {
    try {
      if (!signal.trim()) {
        alert.show("Please enter signal");
        return;
      }
      setLoader(true);
      let payload = { signal };

      const res = await api.generateSummary(payload);
      if (res?.success) {
        setSummary(res?.summary || "");
        alert.show("Summary generated successfully!", { type: 'success' });
      }
      setLoader(false);
    } catch (error) {
      console.log(error?.message);
      alert.show("Failed to generate summary", { type: 'error' });
      setLoader(false);
    }
  };

  const handleCopy = (text) => {
    copy(text);
    setCopyText("Copied!");
    setTimeout(() => {
      setCopyText("");
    }, 3000);
  };

  const handleSignalChange = (e) => {
    setSignal(e.target.value);
    setCharacterCount(e.target.value.length);
  };

  const clearAll = () => {
    setSignal("");
    setSummary("");
    setCharacterCount(0);
  };

  return (
    <>
      <div className="container-fluid">
        {/* Header Section */}
        <div className="trade-summary-header-section mb-5">
          <div className="trade-summary-header-content">
            <div>
              <h1 className="trade-summary-main-title">
                <i className="fas fa-chart-line trade-summary-title-icon me-3"></i>
                Trade Summary Generator
              </h1>
              <p className="trade-summary-subtitle">
                Transform trading signals into comprehensive AI-powered summaries
              </p>
            </div>
            <button className="trade-summary-add-btn" onClick={handleShow}>
              <i className="fas fa-plus-circle me-2"></i>
              Add Summary
            </button>
          </div>
        </div>

        {/* Main Card */}
        <div className="trade-summary-card-container">
          <div className="trade-summary-card-wrapper">
            <div className="trade-summary-card-header">
              <div className="trade-summary-card-header-left">
                <div className="trade-summary-card-icon">
                  {/* <i className="fas fa-signal-alt"></i> */}
                  <ChartNoAxesCombined />
                </div>
                <div>
                  <h3 className="trade-summary-card-title">Signal Analysis</h3>
                  <p className="trade-summary-card-description">
                    Enter your trading signal to generate an AI-powered summary
                  </p>
                </div>
              </div>
              {signal && (
                <button 
                  className="trade-summary-clear-btn"
                  onClick={clearAll}
                  title="Clear all"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>

            <div className="trade-summary-card-body">
              {/* Input Section */}
              <div className="trade-summary-input-section">
                <div className="trade-summary-input-header">
                  <label className="trade-summary-input-label">
                    Trading Signal Input
                  </label>
                  <span className="trade-summary-char-count">
                    {characterCount} / 5000 characters
                  </span>
                </div>
                <div className="trade-summary-textarea-wrapper">
                  <textarea
                    value={signal}
                    onChange={handleSignalChange}
                    className="trade-summary-textarea"
                    placeholder="Paste your trading signal here...
Example: Buy EUR/USD at 1.0850, SL at 1.0820, TP at 1.0920. Bullish divergence on 4H RSI, strong support at 1.0830 level."
                    rows={6}
                    maxLength={5000}
                  />
                  <div className="trade-summary-input-hint">
                    <i className="fas fa-lightbulb me-2"></i>
                    Include entry price, stop loss, take profit, and market context for best results
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="trade-summary-action-btns">
                <button 
                  className={`trade-summary-submit-btn ${loader ? 'processing' : ''}`}
                  onClick={getSummary}
                  disabled={!signal.trim() || loader}
                >
                  {loader ? (
                    <>
                      <span className="trade-summary-spinner"></span>
                      AI is Processing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-magic me-2"></i>
                      Generate AI Summary
                    </>
                  )}
                </button>
                
                {signal && !loader && (
                  <button 
                    className="trade-summary-secondary-btn"
                    onClick={clearAll}
                  >
                    <i className="fas fa-eraser me-2"></i>
                    Clear Input
                  </button>
                )}
              </div>

              {/* Results Section */}
              {summary && (
                <div className="trade-summary-result-section">
                  <div className="trade-summary-result-header">
                    <div className="trade-summary-result-title-section">
                      <div className="trade-summary-result-icon">
                        <i className="fas fa-file-contract"></i>
                      </div>
                      <div>
                        <h4 className="trade-summary-result-title">AI Generated Summary</h4>
                        <p className="trade-summary-result-timestamp">
                          Generated {moment().format('MMM D, YYYY [at] h:mm A')}
                        </p>
                      </div>
                    </div>
                    <button 
                      className={`trade-summary-copy-btn ${copyText ? 'copied' : ''}`}
                      onClick={() => handleCopy(summary)}
                    >
                      <i className="fas fa-copy me-2"></i>
                      {copyText || "Copy Summary"}
                    </button>
                  </div>

                  <div className="trade-summary-content">
                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                      {summary}
                    </ReactMarkdown>
                  </div>

                  <div className="trade-summary-footer">
                    <div className="trade-summary-tags">
                      <span className="trade-summary-tag">
                        <i className="fas fa-robot me-1"></i>
                        AI-Powered Analysis
                      </span>
                      <span className="trade-summary-tag">
                        <i className="fas fa-bolt me-1"></i>
                        Real-time Processing
                      </span>
                      <span className="trade-summary-tag">
                        <i className="fas fa-shield-alt me-1"></i>
                        Professional Grade
                      </span>
                    </div>
                    <div className="trade-summary-usage-tip">
                      <i className="fas fa-info-circle me-2"></i>
                      Use this summary for trade journaling, risk assessment, and performance tracking
                    </div>
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!summary && !loader && (
                <div className="trade-summary-empty-state">
                  <div className="trade-summary-empty-icon">
                    <i className="fas fa-robot"></i>
                  </div>
                  <h4 className="trade-summary-empty-title">Ready to Analyze</h4>
                  <p className="trade-summary-empty-text">
                    Enter a trading signal above to generate a comprehensive AI-powered summary
                  </p>
                </div>
              )}

              {/* Loading State */}
              {loader && (
                <div className="trade-summary-loading-state">
                  <div className="trade-summary-loading-content">
                    <div className="trade-summary-loading-spinner">
                      <div className="trade-summary-spinner-circle"></div>
                    </div>
                    <div>
                      <h5 className="trade-summary-loading-title">AI Analysis in Progress</h5>
                      <p className="trade-summary-loading-text">
                        Processing your signal and generating insights...
                      </p>
                      <div className="trade-summary-progress">
                        <div className="trade-summary-progress-bar"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Summary Modal */}
      <Modal show={show} size="lg" onHide={handleClose} centered>
        <Modal.Body>
          <div className="trade-summary-modal">
            <div className="trade-summary-modal-header">
              <h5 className="trade-summary-modal-title">Add New Summary</h5>
              <button 
                className="trade-summary-modal-close"
                onClick={handleClose}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="trade-summary-modal-body">
              <AddSummary setShow={setShow} />
            </div>
          </div>
        </Modal.Body>
      </Modal>

    </>
  );
}

export default TradeSummary;